import type { MeshNode, Packet } from "../api/types";
import { MAX_CONCURRENT_ANIMATIONS } from './definitions/map';

// Timing (BUILD THIS #5). The spec's own multi-hop timing (§4.1) doesn't
// apply yet — phase 1 has one observer and no carried-route data — so this
// is the simple single-pulse case (spec §4.4's fallback), tuned to feel
// snappy without being so fast it's unclickable.
export const FLIGHT_MS = 700; // marker travels from -> to (only when a receiver position is known — see below)
export const SETTLE_HOLD_MS = 500; // ray fully lit, briefly held
export const FADE_MS = 1600; // ray fade-out
export const CLICKABLE_MS = FLIGHT_MS + SETTLE_HOLD_MS + FADE_MS; // whole in-flight lifetime, hit-testable per §4.5

// Origin pulse ring — retuned after live packets weren't visibly
// registering on screen. Two compounding causes, both measured, not
// guessed: (1) 92% of sampled live packets are broadcasts (`to_id:
// "^all"`), which used to mean no receiver could ever be resolved and no
// ray was ever drawn — see AppData.tsx's addPacket for the fix (the ray
// now targets the real station that heard it, not the packet's declared
// `to_id`, which was never a resolvable position for a broadcast to begin
// with). (2) The ring itself — the one thing every packet gets, broadcast
// or not — had been tuned quiet after an earlier complaint about dense
// glyphs: 6-20px, capped at a flat 0.4 opacity, fading over RING_TOTAL_MS
// (formerly 4200ms). That's not a bright pulse fading from visible to
// gone — it STARTS at 0.4 and only gets dimmer, on a map already covered
// in ~1,600 positioned dots. It rendered correctly and could not be seen.
//
// The call: a bright pulse on every packet, tuned so it doesn't
// reaccumulate into the original glyph-mass complaint — shorter and
// brighter beats larger and longer, explicitly not a reversion to the
// old 34px/0.85 values. So both axes move, but by different amounts:
//   - PULSE_MS: 4200ms -> 900ms. At the live rate (~32 packets/min, one
//     every ~1.9s on average), a pulse that's gone in under a second
//     reads as a distinct flash-per-packet instead of a slow smear that
//     overlaps its own successors.
//   - PULSE_PEAK_OPACITY: 0.4 -> 0.85. The ring now STARTS near-opaque
//     and fades to 0 — a flash, not a dim haze — while landing just under
//     the old 0.85-for-4200ms combination's brightness*duration product,
//     since duration dropped by more than opacity rose.
//   - PULSE_MAX_SIZE_PX: 20 -> 22, PULSE_MIN_SIZE_PX: 6 -> 4. A slightly
//     wider growth range for a touch more presence, nowhere near the old
//     34px — size was a smaller contributor to the original complaint
//     than duration was, so it moves the least.
//   - PULSE_STROKE_PX: 1 -> 2. A 1px ring on a hi-DPI map basemap all but
//     disappears at these sizes regardless of opacity; 2px keeps the ring
//     legible as a ring rather than a soft dot.
// This is a genuinely different combination from "restore the old
// values," not a rounding of them.
export const PULSE_MS = 900;
export const PULSE_PEAK_OPACITY = 0.85;
export const PULSE_MIN_SIZE_PX = 4;
export const PULSE_MAX_SIZE_PX = 22;
export const PULSE_STROKE_PX = 2;

/** How long one animation entry stays alive — the pulse-only case (no
 * resolved receiver position; the large majority of live traffic) needs
 * only long enough to show its own short flash, not the full ray
 * lifetime it will never use. A packet whose receiver WAS resolved keeps
 * the longer CLICKABLE_MS lifetime, since its ray/marker genuinely
 * animate for that long and must stay hit-testable (spec §4.5) for as
 * long as they're visible. Shortening the common (pulse-only) case's
 * lifetime also means the concurrency cap (MAX_CONCURRENT_ANIMATIONS)
 * turns over faster in the case that dominates real traffic, rather than
 * every broadcast pulse occupying a slot for the same 2.8s a real ray
 * needs. */
export function animationLifetimeMs(anim: Pick<AnimatedPacket, "to">): number {
  return anim.to ? CLICKABLE_MS : PULSE_MS;
}

export interface AnimatedPacket {
  key: string;
  packet: Packet;
  from: MeshNode | null;
  // The RECEIVING station's node, when its position is known — never the
  // packet's own declared `to_id` (meaningless for the 92% of packets
  // that are broadcasts, and not what this ray represents even when a
  // packet does name a unicast destination we haven't necessarily heard
  // from). See AppData.tsx's addPacket/replaySinglePacket for how this is
  // resolved: our own station's node for a packet we heard directly, null
  // (pulse-only, never a guessed endpoint) when the receiver or its
  // position isn't known — e.g. an MQTT-relayed packet, whose relaying
  // gateway isn't part of the live packet stream this app receives (only
  // available via a separate per-packet coverage fetch, checked directly
  // against the real payload — not wired into real-time animation, since
  // that would mean a network round-trip per packet before any ray could
  // ever be drawn).
  to: MeshNode | null;
  createdAt: number;
}

let counter = 0;

export function makeAnimatedPacket(
  packet: Packet,
  from: MeshNode | null,
  to: MeshNode | null,
): AnimatedPacket {
  counter += 1;
  return {
    key: `${packet.id ?? packet.hash ?? "pkt"}-${counter}`,
    packet,
    from,
    to,
    createdAt: performance.now(),
  };
}

/** Enforce the concurrency cap; returns [keep, overflowed]. */
export function capAnimations(
  existing: AnimatedPacket[],
  incoming: AnimatedPacket,
): { next: AnimatedPacket[]; overflowed: boolean } {
  if (existing.length >= MAX_CONCURRENT_ANIMATIONS) {
    return { next: existing, overflowed: true };
  }
  return { next: [...existing, incoming], overflowed: false };
}

export function pruneExpired(list: AnimatedPacket[], now: number): AnimatedPacket[] {
  return list.filter((a) => now - a.createdAt < animationLifetimeMs(a));
}

// --- meshview route-tree walk timing (live map, 2026-09-21) ---------------
// See lib/definitions.ts's "meshview route-tree walk" comment for the full
// design (single GL source, not a per-edge DOM animation like the ray
// above). Superseded the original flat transmitter-to-every-feeder fan
// twice over, in order:
//
// 1) The concept error: a straight line from the
//    transmitter to a 6-hop-away feeder draws a packet crossing the whole
//    map in one bound, which never happened — a packet should never hop
//    clear across the entire map; it needs to flow through the heard
//    feeders. The backend's route-tree
//    endpoint (Task 27) exists for exactly this: it infers the most
//    plausible parent->child ancestry among nodes actually observed, so
//    each edge is one short adjacent-node hop, never transmitter-to-
//    distant-feeder. lib/meshview.ts's buildRouteWalk turns that tree into
//    a flat list of edges, each carrying its OWN precomputed release
//    offset (a real tree traversal, not a formula here) — this file only
//    supplies the reusable per-edge travel/hold/fade state machine below.
//
// 2) Separately, a pixel measurement of the shipped (flat-fan)
//    build found a median frame at baseline — essentially an empty map —
//    because the real arrival cadence (~10 packets/min, one every ~6s) far
//    outlasted each fan's ~2.8s total visible lifetime (HOLD 4000 + FADE
//    2000 was too short once travel was added on top). HOLD/FADE were made
//    bespoke (2600/2200), deliberately NOT reused from the ray's short
//    FLIGHT_MS/SETTLE_HOLD_MS/FADE_MS, specifically to keep one packet's
//    walk visible long enough to overlap the next one's — i.e. to paper
//    over the gap between arrivals.
//
// 3) Superseded again (quiet spaces are OK if they're truly
//    quiet): measuring 300 consecutive live packets' own imported_at
//    timestamps found the mesh is a steady ~4s trickle (median gap 3.91s),
//    not bursty — the observed burstiness was entirely the queue
//    scheduler's own even-spreading (see the drain-scheduler section
//    below), not a real density problem needing HOLD/FADE to paper over.
//    Once that scheduler was replaced with a real-timestamp replay, gaps
//    are honestly reproduced on screen (including real quiet stretches),
//    so there is no gap left to paper over — HOLD/FADE are realigned to
//    what reads well for ONE packet, i.e. exactly the ray's own
//    SETTLE_HOLD_MS/FADE_MS values (this is genuinely a reversion, not a
//    new tuning: it's the same combination pass (1) used before the
//    since-reversed "stream evenly" direction).
export const MESHVIEW_RECEPTION_HOLD_MS = SETTLE_HOLD_MS;
export const MESHVIEW_RECEPTION_FADE_MS = FADE_MS;

// How far apart (in ms) siblings sharing the same parent node in the
// route-tree spread their own launches — within a level, keep the
// spread already applied so the 11 children of one node launch in
// sequence, not as a clump. Ranked by each edge's own rx_snr (descending
// — the same secondary ordering the flat /receptions endpoint itself uses
// server-side), not array position, since the route-tree contract carries
// no per-edge timestamp to order by honestly. A proportional (index/count)
// spread across this window, not a flat per-item constant, so a large
// sibling group still finishes launching within one window and a small one
// doesn't spread needlessly wide — same shape as the retired hop-based
// stagger this replaces, just keyed by tree parentage instead of raw hop
// count (lib/meshview.ts's buildRouteWalk).
export const MESHVIEW_ROUTE_SIBLING_SPREAD_MS = 900;

// --- meshview full-route comet trails (live map, 2026-09-21) --------------
// The packets flying need a line trail: they leave the
// path they travel behind them, slowly fading away from the
// starting point of the tail to the final path, flowing all the
// way through the route. Two changes from the per-edge version above: (1)
// one traveller per reached node walks its FULL path (root through every
// ancestor to that node) as one continuous motion — constant time per
// segment, so a 7-hop journey genuinely takes longer than a 1-hop one —
// rather than each edge animating in isolation; (2) it leaves a trail that
// dissipates from its OLDEST end forward (the tail retreats as the head
// advances), not a uniform fade. MapLibre's `line-gradient` is a paint
// property on the whole LAYER, not per-feature, so ~dozens of simultaneous
// per-traveller gradients can't share one line layer that way — the trail
// is instead approximated as a short run of small LineString slices, each
// carrying its own `opacity` property, data-driven via `line-opacity`
// (LiveMap.tsx). Shared trunks (every path out of a busy packet crosses the
// same first edge or two) are expected and desirable — many overlapping
// travellers reading brighter there is honest, since that's genuinely where
// the traffic went — so HEAD_OPACITY is capped well below 1 to leave
// headroom before overlapping slices blow out to a solid slab.
export const MESHVIEW_TRAIL_MS = 1100; // how much travel-time the trail spans behind the head
export const MESHVIEW_TRAIL_SLICES = 6; // small LineString features approximating the opacity gradient
export const MESHVIEW_TRAIL_HEAD_OPACITY = 0.7; // peak (head-end) opacity multiplier, before confidence styling

export interface RoutePathState {
  phase: "travelling" | "settled" | "fading";
  /** Elapsed ms since this path's own traveller departed the root, clamped
   * to [0, totalTravelMs] — lib/meshview.ts's positionAlongPath turns this
   * into a head position (and, while travelling, a trail) by walking the
   * path's own segments. Always `totalTravelMs` once settled/fading (the
   * traveller has fully arrived; only whole-path opacity changes after
   * that). */
  elapsedIntoTravel: number;
  /** Opacity multiplier for the SETTLED/FADING full-path render. The
   * travelling phase computes its own per-slice trail opacity separately
   * (MESHVIEW_TRAIL_HEAD_OPACITY above) rather than using this field. */
  opacity: number;
}

/** One route-tree PATH's (root -> ... -> this node) own travel/hold/fade
 * state `elapsedMs` after its packet's walk was created. `startOffsetMs` is
 * this path's own precomputed departure delay (lib/meshview.ts's
 * buildRouteWalk — a tree traversal: paths sharing a parent still spread
 * their departures via MESHVIEW_ROUTE_SIBLING_SPREAD_MS, "so the paths do
 * not all launch together"). `totalTravelMs` is this path's own segment
 * count times FLIGHT_MS (constant time per hop — a longer journey
 * genuinely takes longer). Returns null before the path is due to depart,
 * or once it has fully faded — the caller should render nothing for it (an
 * absent GeoJSON feature, not a zero-opacity one, keeps the per-frame
 * feature list small). Hold/fade are this file's own
 * MESHVIEW_RECEPTION_HOLD_MS/FADE_MS, tuned for sustained coverage against
 * the real arrival rate rather than the ray's own snappy single-packet
 * timing — see this section's header comment. */
export function routeWalkPathState(startOffsetMs: number, totalTravelMs: number, elapsedMs: number): RoutePathState | null {
  const t = elapsedMs - startOffsetMs;
  if (t < 0) return null;
  if (t < totalTravelMs) return { phase: "travelling", elapsedIntoTravel: t, opacity: 1 };
  const sinceHold = t - totalTravelMs;
  if (sinceHold < MESHVIEW_RECEPTION_HOLD_MS) return { phase: "settled", elapsedIntoTravel: totalTravelMs, opacity: 1 };
  const sinceFadeStart = sinceHold - MESHVIEW_RECEPTION_HOLD_MS;
  if (sinceFadeStart >= MESHVIEW_RECEPTION_FADE_MS) return null;
  return { phase: "fading", elapsedIntoTravel: totalTravelMs, opacity: Math.max(0, 1 - sinceFadeStart / MESHVIEW_RECEPTION_FADE_MS) };
}

// --- meshview adaptive concurrency cap (live map, 2026-09-21) -------------
// A lot of concurrent packets, especially during heavy traffic, reads as
// good density on the map — bias hard toward density. A hardcoded cap derived from a
// software-rendered benchmark (this project's own earlier approach here)
// systematically understates what a real GPU can do, throttling every real
// user to protect a worst case that doesn't occur for them. Replaced with a
// runtime-adaptive cap: start high, measure ACTUAL frame time on whatever
// machine is actually running it (LiveMap.tsx reports each frame's own
// delta via AppData.tsx's reportMeshviewFrameMs), and scale down only if
// that specific device is genuinely struggling — recovering when it isn't.
// See AppData.tsx's own adjustment effect for the controller; these are
// just its tuned parameters, kept here (not hardcoded in that effect) per
// the standing "configurable, not hardcoded" rule.
export const MESHVIEW_CAP_DEFAULT = 128;
export const MESHVIEW_CAP_MIN = 16;
export const MESHVIEW_CAP_MAX = 320;
export const MESHVIEW_CAP_STEP = 12;
// Frame-budget thresholds the controller reacts to (ms/frame — 16.7ms is
// 60fps). Above SLOW the device is visibly struggling and the cap steps
// down; below FAST there's comfortable headroom and it steps back up. The
// gap between the two (not one shared threshold) keeps it from oscillating
// a step up and down every single adjustment window.
export const MESHVIEW_FRAME_MS_SLOW_THRESHOLD = 22;
export const MESHVIEW_FRAME_MS_FAST_THRESHOLD = 14;
export const MESHVIEW_CAP_ADJUST_INTERVAL_MS = 1000;
export const MESHVIEW_CAP_MIN_SAMPLES = 20;

// --- meshview firehose playback scheduler (live map animation fix, 2026-09-21) --
// Observed on the live map: packets only appeared to arrive at the local
// station, then once caught up they'd all suddenly appear and fade at
// once — as if bulk-importing them. Confirmed cause:
// AppData.tsx's firehose poll effect used to loop over every newly-seen
// packet in one poll response and call setAnimations synchronously for
// each, in the same tick — a whole batch started (and later expired)
// together.
//
// First fix (superseded): queue newly-seen packets and drain
// them spaced by the REAL gap between their own imported_at timestamps,
// undelayed. That faithfully reproduced the burstiness rather than fixing
// it — a pixel measurement of that build found a median frame at
// baseline (essentially empty); this was ultimately a simulation of
// packets moving, not a truth.
//
// Second fix (2026-09-21, superseded): pace the drain adaptively off queue
// depth and a learned estimate of how often a fresh batch lands, evenly
// spreading playback across that window instead of following real
// per-packet gaps at all. This eliminated the lump but over-corrected: a
// genuine lull got filled in and a genuine flurry got thinned, in both
// directions a lie.
//
// Third fix, current (quiet spaces are OK if they're truly
// quiet — fidelity beats density): measuring 300 consecutive live packets'
// own imported_at timestamps showed the real mesh is a steady trickle
// (11.1 packets/min, median gap 3.91s, only 1 gap over 30s in 27 minutes) —
// the burstiness on screen was never a real density problem, it was
// entirely the batch-fetch artifact of the backend's own upstream poll
// (routes/meshview.py, ~60s cycle, up to 50 rows at once) landing in our
// own 2s poll as a lump with no relation to when the packets actually
// happened. The fix is a REPLAY, not a re-pacing: each packet is released
// at its own imported_at, delayed by a fixed MESHVIEW_PLAYBACK_LAG_MS —
// never spread evenly, never paced by queue depth, gaps never clamped.
// AppData.tsx's scheduler keeps one queue (sorted by each packet's own
// scheduled release time) and releases everything whose time has come on
// each MESHVIEW_QUEUE_TICK_MS tick; the "gap between releases" that
// produces is just the real gap between the packets' own timestamps, lag
// cancels out identically on both ends of a subtraction. If the queue runs
// dry (a real lull, or our own poll hiccuping), the map goes quiet — that
// is correct, not a bug to paper over.
export const MESHVIEW_QUEUE_TICK_MS = 100; // how often the scheduler checks for packets whose scheduled release time has arrived
// Fixed replay lag: how far behind real (wall-clock) time the playback
// clock runs. Chosen against the backend's own worst-case latency, not
// guessed: a packet's imported_at is set the moment meshview's upstream
// import happens, but it isn't visible via GET /api/meshview/packets until
// the backend's own upstream poll cycle (~60s, routes/meshview.py) next
// runs — worst case just under 60s after the fact — plus our own 2s poll
// of the backend. 90s leaves a comfortable margin (>1 full backend poll
// cycle) over that ~62s worst case, so a packet is essentially always
// already in hand well before its scheduled release moment arrives.
export const MESHVIEW_PLAYBACK_LAG_MS = 90_000;
// Hard cap on the pending queue itself — separate from
// MAX_CONCURRENT_ANIMATIONS above, which caps rendered animations, not
// packets waiting to be released. Never grow an unbounded backlog: once
// full, the oldest queued packets are dropped (least relevant — the point
// is to keep pace with real time) and counted via the existing overflow
// counter, same "count it, don't silently lose it" convention the
// animation cap already follows.
export const MESHVIEW_QUEUE_MAX = 100;
